import React from "react";
import { Badge, Card, Col, Row, Table } from "react-bootstrap";
import { HelpLink } from "./help/HelpNavigation.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faBookOpen,
  faChartColumn,
  faCircleNodes,
  faListCheck,
  faRoute,
  faTableColumns,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import {
  GUIDE_PAGE_MAP,
  GUIDE_PAGES,
  GUIDE_PILLARS,
  GUIDE_TRAILS,
  PLATFORM02_NOTIFICATION_CATALOG,
  PLATFORM02_NOTIFICATION_DEVICE_COMPATIBILITY,
  PLATFORM02_NOTIFICATION_FLOW,
  PLATFORM02_NOTIFICATION_GUIDE_SECTIONS,
  PLATFORM02_IMPORT_FIELD_RULES,
  PLATFORM02_IMPORT_GUIDE_SECTIONS,
  PLATFORM02_IMPORT_VALIDATION_GUIDE,
  guideHeadingId,
} from "./Platform02GuideData.js";
import {
  LEGACY_GUIDE_OVERVIEW,
  LEGACY_GUIDE_PAGES,
} from "./Platform02LegacyGuideData.js";
import {
  PLATFORM02_ACHIEVEMENT_COUNT,
  PLATFORM02_SCORE_GROUPS,
  PLATFORM02_ACHIEVEMENT_GROUPS,
  PLATFORM02_SCORE_RULES,
} from "../utils/platform02GuideGamification.js";

export const headingId = guideHeadingId;

const guideLinkProps = (linkState, onNavigate) => ({
  state: linkState,
  onClick: onNavigate,
});

const GuideBreadcrumb = ({ page, linkState, onNavigate }) => (
  <nav className="platform02-guide-breadcrumb" aria-label="Caminho da página">
    {page.path.map((item, index) => {
      const isCurrent = index === page.path.length - 1;
      return (
        <React.Fragment key={item.id}>
          {index > 0 && <span aria-hidden="true">/</span>}
          {isCurrent ? (
            <span aria-current="page">{item.title}</span>
          ) : (
            <HelpLink to={item.href} {...guideLinkProps(linkState, onNavigate)}>
              {item.title}
            </HelpLink>
          )}
        </React.Fragment>
      );
    })}
  </nav>
);

const GuideHeader = ({ page, linkState, onNavigate }) => (
  <>
    <GuideBreadcrumb page={page} linkState={linkState} onNavigate={onNavigate} />
    <div className="platform02-guide-page-heading">
      <div>
        <div className="platform02-guide-eyebrow">
          {page.kind === "module" ? "Módulo" : page.kind === "hub" ? "Categoria" : "Página"}
        </div>
        <h1>{page.title}</h1>
        <p className="platform02-guide-page-intro">{page.description}</p>
      </div>
      {page.status && <Badge bg="light" text="dark">{page.status}</Badge>}
    </div>
  </>
);

const GuideCardLink = ({ item, index, linkState, onNavigate }) => (
  <Card
    as={HelpLink}
    to={item.href}
    className="platform02-guide-link-card text-decoration-none"
    {...guideLinkProps(linkState, onNavigate)}
  >
    <Card.Body>
      <div className="platform02-guide-link-card-number">
        {String(index + 1).padStart(2, "0")}
      </div>
      <h2>{item.title}</h2>
      <p>{item.description}</p>
      <span className="platform02-guide-card-action">
        Abrir página <FontAwesomeIcon icon={faArrowRight} />
      </span>
    </Card.Body>
  </Card>
);

const GuideHome = ({ linkState, onNavigate }) => (
  <div className="platform02-guide-home">
    <section className="platform02-guide-hero">
      <div className="platform02-guide-eyebrow">Plataforma de eventos</div>
      <h1>Guia comercial e operacional</h1>
      <p>
        Estrutura de navegação para apresentar o ecossistema, montar a experiência
        e acompanhar o evento do início aos resultados.
      </p>
      <div className="platform02-guide-hero-status">
        <FontAwesomeIcon icon={faBookOpen} />
        Estrutura pronta para receber o conteúdo
      </div>
    </section>

    <section className="platform02-guide-home-section" aria-labelledby="guide-ecosystem-title">
      <div className="platform02-guide-section-heading">
        <div>
          <span>Visão do produto</span>
          <h2 id="guide-ecosystem-title">O ecossistema em uma jornada</h2>
        </div>
      </div>
      <div className="platform02-guide-ecosystem" aria-label="Fluxo do ecossistema">
        {["Painel", "Networking", "Credenciamento", "Resultados"].map((item, index, items) => (
          <React.Fragment key={item}>
            <div className="platform02-guide-ecosystem-step">
              <span>{index + 1}</span>
              <strong>{item}</strong>
            </div>
            {index < items.length - 1 && <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />}
          </React.Fragment>
        ))}
      </div>
    </section>

    <section className="platform02-guide-home-section" aria-labelledby="guide-pillars-title">
      <div className="platform02-guide-section-heading">
        <div>
          <span>Conheça a plataforma</span>
          <h2 id="guide-pillars-title">Cinco pilares do guia</h2>
        </div>
        <FontAwesomeIcon icon={faCircleNodes} aria-hidden="true" />
      </div>
      <Row className="g-3">
        {GUIDE_PILLARS.map((pillar, index) => (
          <Col xl={index < 2 ? 6 : 4} md={6} key={pillar.id}>
            <GuideCardLink item={pillar} index={index} linkState={linkState} onNavigate={onNavigate} />
          </Col>
        ))}
      </Row>
    </section>

    <section className="platform02-guide-home-section" aria-labelledby="guide-trails-title">
      <div className="platform02-guide-section-heading">
        <div>
          <span>Navegação guiada</span>
          <h2 id="guide-trails-title">Trilhas recomendadas</h2>
        </div>
        <FontAwesomeIcon icon={faRoute} aria-hidden="true" />
      </div>
      <div className="platform02-guide-trails">
        {GUIDE_TRAILS.map((trail) => (
          <article className="platform02-guide-trail" key={trail.id}>
            <h3>{trail.title}</h3>
            <p>{trail.description}</p>
            <div className="platform02-guide-trail-steps">
              {trail.pages.map((pageId, index) => {
                const trailPage = GUIDE_PAGE_MAP[pageId];
                return (
                  <React.Fragment key={pageId}>
                    <HelpLink to={trailPage.href} {...guideLinkProps(linkState, onNavigate)}>
                      {trailPage.title}
                    </HelpLink>
                    {index < trail.pages.length - 1 && <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />}
                  </React.Fragment>
                );
              })}
            </div>
          </article>
        ))}
      </div>
    </section>
  </div>
);

const GuideHub = ({ page, linkState, onNavigate }) => (
  <>
    <GuideHeader page={page} linkState={linkState} onNavigate={onNavigate} />
    <div className="platform02-guide-hub-summary">
      <FontAwesomeIcon icon={page.kind === "module" ? faCircleNodes : faBookOpen} />
      <div>
        <strong>{page.kind === "module" ? "Padrão de módulo" : "Índice da categoria"}</strong>
        <span>Títulos e descrições curtas direcionam para páginas independentes.</span>
      </div>
    </div>
    <Row className="g-3 platform02-guide-hub-grid">
      {(page.children || []).map((item, index) => (
        <Col xl={6} key={item.id}>
          <GuideCardLink item={item} index={index} linkState={linkState} onNavigate={onNavigate} />
        </Col>
      ))}
    </Row>
  </>
);

const EmptyLines = ({ compact = false }) => (
  <div className={`platform02-guide-sample-lines ${compact ? "compact" : ""}`} aria-hidden="true">
    <span />
    <span />
    <span />
  </div>
);

const SectionTitle = ({ page, section, index }) => (
  <div className="platform02-guide-sample-heading" id={guideHeadingId(page.id, section)}>
    <span>{String(index + 1).padStart(2, "0")}</span>
    <h2>{section}</h2>
  </div>
);

const ShowcaseSample = ({ page }) => {
  const [featured, ...remaining] = page.sections;
  return (
    <div className="platform02-guide-sample platform02-guide-sample-showcase">
      {featured && (
        <section className="platform02-guide-showcase-feature">
          <SectionTitle page={page} section={featured} index={0} />
          <div className="platform02-guide-showcase-media" aria-hidden="true" />
          <EmptyLines />
        </section>
      )}
      <div className="platform02-guide-showcase-grid">
        {remaining.map((section, index) => (
          <section key={section}>
            <SectionTitle page={page} section={section} index={index + 1} />
            <EmptyLines compact />
          </section>
        ))}
      </div>
    </div>
  );
};

const WorkflowSample = ({ page }) => (
  <div className="platform02-guide-sample platform02-guide-sample-workflow">
    {page.sections.map((section, index) => (
      <section key={section}>
        <div className="platform02-guide-workflow-marker">{index + 1}</div>
        <div className="platform02-guide-workflow-content">
          <SectionTitle page={page} section={section} index={index} />
          <EmptyLines compact />
        </div>
      </section>
    ))}
  </div>
);

const CatalogSample = ({ page }) => (
  <div className="platform02-guide-sample platform02-guide-sample-catalog">
    <div className="platform02-guide-catalog-toolbar" aria-hidden="true">
      <span />
      <span />
    </div>
    <div className="platform02-guide-catalog-grid">
      {page.sections.map((section, index) => (
        <section key={section}>
          <div className="platform02-guide-catalog-icon" aria-hidden="true" />
          <SectionTitle page={page} section={section} index={index} />
          <EmptyLines compact />
        </section>
      ))}
    </div>
  </div>
);

const ComparisonSample = ({ page }) => (
  <div className="platform02-guide-sample platform02-guide-sample-comparison">
    <div className="platform02-guide-comparison-legend">
      <span>Opção</span><span>Aplicação</span><span>Cuidados</span>
    </div>
    {page.sections.map((section, index) => (
      <section key={section}>
        <SectionTitle page={page} section={section} index={index} />
        <div aria-hidden="true" /><div aria-hidden="true" />
      </section>
    ))}
  </div>
);

const DashboardSample = ({ page }) => {
  const metrics = page.sections.slice(0, 3);
  const panels = page.sections.slice(3);
  return (
    <div className="platform02-guide-sample platform02-guide-sample-dashboard">
      <div className="platform02-guide-dashboard-metrics">
        {metrics.map((section, index) => (
          <section key={section}>
            <SectionTitle page={page} section={section} index={index} />
            <div className="platform02-guide-dashboard-value" aria-hidden="true" />
          </section>
        ))}
      </div>
      <div className="platform02-guide-dashboard-panels">
        {panels.map((section, index) => (
          <section key={section}>
            <SectionTitle page={page} section={section} index={index + metrics.length} />
            <div className="platform02-guide-dashboard-chart" aria-hidden="true" />
          </section>
        ))}
      </div>
    </div>
  );
};

const TroubleshootingSample = ({ page }) => (
  <div className="platform02-guide-sample platform02-guide-sample-troubleshooting">
    {page.sections.map((section, index) => (
      <section key={section}>
        <SectionTitle page={page} section={section} index={index} />
        <div className="platform02-guide-diagnostic-flow">
          <span>Sintoma</span><FontAwesomeIcon icon={faArrowRight} /><span>Verificar</span>
          <FontAwesomeIcon icon={faArrowRight} /><span>Orientação</span>
        </div>
      </section>
    ))}
  </div>
);

const ReferenceSample = ({ page }) => (
  <div className="platform02-guide-sample platform02-guide-sample-reference">
    {page.sections.map((section, index) => (
      <section key={section}>
        <SectionTitle page={page} section={section} index={index} />
        <div className="platform02-guide-reference-body">
          <EmptyLines />
          <div className="platform02-guide-reference-note"><span>Exemplo</span></div>
          <div className="platform02-guide-reference-note"><span>Regras e cuidados</span></div>
        </div>
      </section>
    ))}
  </div>
);

const SAMPLE_LAYOUTS = {
  showcase: ShowcaseSample,
  workflow: WorkflowSample,
  catalog: CatalogSample,
  comparison: ComparisonSample,
  dashboard: DashboardSample,
  troubleshooting: TroubleshootingSample,
  reference: ReferenceSample,
};

const SAMPLE_LAYOUT_LABELS = {
  showcase: "Vitrine",
  workflow: "Fluxo",
  catalog: "Catálogo",
  comparison: "Comparação",
  dashboard: "Painel",
  troubleshooting: "Diagnóstico",
  reference: "Referência",
};

const GuideArticle = ({ page, linkState, onNavigate }) => {
  const SampleLayout = SAMPLE_LAYOUTS[page.layout] || ReferenceSample;
  return (
    <>
      <GuideHeader page={page} linkState={linkState} onNavigate={onNavigate} />
      <div className="platform02-guide-layout-label">
        <FontAwesomeIcon icon={page.layout === "dashboard" ? faChartColumn : page.layout === "comparison" ? faTableColumns : page.layout === "troubleshooting" ? faTriangleExclamation : faListCheck} />
        <span>Amostra de composição</span>
        <strong>{SAMPLE_LAYOUT_LABELS[page.layout] || SAMPLE_LAYOUT_LABELS.reference}</strong>
      </div>
      <SampleLayout page={page} />
    </>
  );
};

const GuideTable = ({ table }) => (
  <Table responsive hover className="platform02-guide-topic-table">
    <thead><tr>{table.headers.map((header) => <th key={header}>{header}</th>)}</tr></thead>
    <tbody>
      {table.rows.map((row, rowIndex) => (
        <tr key={`${row[0]}-${rowIndex}`}>
          {row.map((cell, cellIndex) => <td key={`${cell}-${cellIndex}`}>{cell}</td>)}
        </tr>
      ))}
    </tbody>
  </Table>
);

const ImportGuide = ({ page, linkState, onNavigate }) => {
  const errorsByCategory = PLATFORM02_IMPORT_VALIDATION_GUIDE.reduce((groups, item) => {
    if (!groups[item.category]) groups[item.category] = [];
    groups[item.category].push(item);
    return groups;
  }, {});

  return (
    <>
      <GuideHeader page={page} linkState={linkState} onNavigate={onNavigate} />

      <section
        id={guideHeadingId(page.id, PLATFORM02_IMPORT_GUIDE_SECTIONS[0])}
        className="platform02-guide-ranking-intro"
      >
        <div className="platform02-guide-eyebrow">{"Fluxo seguro"}</div>
        <h2>{"Uma planilha, uma validação completa, uma transação"}</h2>
        <p>
          {"A importação usa o formulário do evento como contrato. O navegador lê o arquivo, valida as linhas e mostra um relatório; a API faz uma pré-validação com os valores atuais e repete as checagens no salvamento. Em um erro normal, nenhum participante, e-mail ou alteração do evento é gravado."}
        </p>
        <Row className="g-3 mt-1">
          <Col md={4}>
            <Card className="platform02-guide-concept-card h-100"><Card.Body>
              <span className="platform02-guide-concept-label">{"1 · Arquivo"}</span>
              <h3>{"Ler e mapear"}</h3>
              <p>{"O arquivo precisa ser .xlsx, .xls ou .csv, ter uma única planilha e cabeçalhos sem ambiguidades."}</p>
            </Card.Body></Card>
          </Col>
          <Col md={4}>
            <Card className="platform02-guide-concept-card h-100"><Card.Body>
              <span className="platform02-guide-concept-label">{"2 · Linha"}</span>
              <h3>{"Validar o formulário"}</h3>
              <p>{"No modo normal, cada linha passa pelas mesmas regras Yup do cadastro, incluindo dependências e duplicidades."}</p>
            </Card.Body></Card>
          </Col>
          <Col md={4}>
            <Card className="platform02-guide-concept-card h-100"><Card.Body>
              <span className="platform02-guide-concept-label">{"3 · Servidor"}</span>
              <h3>{"Confirmar e salvar"}</h3>
              <p>{"A API revalida e-mails, conflitos, vagas e limite de inscrições; só então grava o lote inteiro."}</p>
            </Card.Body></Card>
          </Col>
        </Row>
        <div className="platform02-guide-callout mb-0">
          <strong>{"Qual configuração é usada?"}</strong>{" Ao escolher o arquivo, o navegador usa os valores que estão sendo editados no formulário (fields e json atuais). A pré-validação também recebe esse JSON atual. No salvamento, a API trava as linhas de vagas e confere o estado efetivo do banco para evitar corrida de capacidade."}
        </div>
      </section>

      <section
        id={guideHeadingId(page.id, PLATFORM02_IMPORT_GUIDE_SECTIONS[1])}
        className="platform02-guide-rich-section"
      >
        <div className="platform02-guide-section-kicker">{"Arquivo"}</div>
        <h2>{"Formato e modelo"}</h2>
        <p>{"O modelo baixado pelo botão Baixar modelo contém somente a linha de cabeçalhos. Os comentários dos cabeçalhos indicam tipo, obrigatoriedade, opções permitidas e se a opção controla vagas; ele não contém um participante de exemplo."}</p>
        <div className="platform02-guide-path"><strong>{"No Painel:"}</strong>{" Evento → Inscrições → Adicionar registros → Importar participantes"}</div>
        <Row className="g-3 platform02-guide-operation-grid">
          <article>
            <h3>{"Colunas aceitas"}</h3>
            <ul>
              <li>{"Plataforma 2: uma coluna reservada chamada email (obrigatória)."}</li>
              <li>{"Os demais títulos devem ser exatamente os rótulos dos campos visíveis do formulário."}</li>
              <li>{"Campos com hideFieldOnRegister ficam fora do modelo e não podem ser importados."}</li>
            </ul>
          </article>
          <article>
            <h3>{"Estrutura mínima"}</h3>
            <ul>
              <li>{"Use apenas uma aba e a primeira linha como cabeçalho."}</li>
              <li>{"Não deixe colunas sem título, repetidas ou desconhecidas."}</li>
              <li>{"Remova linhas de exemplo e preserve células vazias apenas em campos opcionais."}</li>
            </ul>
          </article>
        </Row>
      </section>

      <section
        id={guideHeadingId(page.id, PLATFORM02_IMPORT_GUIDE_SECTIONS[2])}
        className="platform02-guide-rich-section"
      >
        <div className="platform02-guide-section-kicker">{"Regras do formulário"}</div>
        <h2>{"Validações por linha"}</h2>
        <p>{"A tabela abaixo separa o que o navegador faz no modo normal e o que muda no modo de emergência. O servidor mantém suas próprias checagens em ambos os modos."}</p>
        <Card className="platform02-guide-catalog-card"><Card.Body>
          <Table responsive hover className="platform02-guide-catalog-table mb-0">
            <thead><tr><th scope="col">{"Campo/regra"}</th><th scope="col">{"Normal (navegador)"}</th><th scope="col">{"Emergência (navegador)"}</th><th scope="col">{"Como corrigir"}</th></tr></thead>
            <tbody>{PLATFORM02_IMPORT_FIELD_RULES.map((rule) => (
              <tr key={rule.id}>
                <th scope="row">{rule.label}</th>
                <td>{rule.normal}</td>
                <td>{rule.emergency}</td>
                <td>{rule.fix}</td>
              </tr>
            ))}</tbody>
          </Table>
        </Card.Body></Card>
      </section>

      <section
        id={guideHeadingId(page.id, PLATFORM02_IMPORT_GUIDE_SECTIONS[3])}
        className="platform02-guide-rich-section"
      >
        <div className="platform02-guide-section-kicker">{"Recuperação"}</div>
        <h2>{"Modo de emergência"}</h2>
        <p>{"Use somente para recuperação de uma base conhecida. Ele altera apenas as validações de conteúdo executadas pelo navegador; não transforma a importação em um bypass do servidor."}</p>
        <Card className="platform02-guide-catalog-card"><Card.Body>
          <Table responsive hover className="platform02-guide-catalog-table mb-0">
            <thead><tr><th scope="col">{"Verificação"}</th><th scope="col">{"Modo normal"}</th><th scope="col">{"Modo de emergência"}</th></tr></thead>
            <tbody>
              <tr><th scope="row">{"Arquivo e cabeçalhos"}</th><td>{"Sempre bloqueia arquivo ilegível, abas extras, cabeçalhos vazios/repetidos/desconhecidos e obrigatórios ausentes."}</td><td>{"Continua bloqueando. Um arquivo malformado nunca é aceito."}</td></tr>
              <tr><th scope="row">{"Campos do formulário"}</th><td>{"Executa as regras Yup para obrigatoriedade, formato, opções, limites e dependências."}</td><td>{"Ignora essas regras no navegador para os campos comuns."}</td></tr>
              <tr><th scope="row">{"Coluna email (Plataforma 2)"}</th><td>{"Valida presença, formato e domínio permitido."}</td><td>{"Continua validada localmente e no servidor."}</td></tr>
              <tr><th scope="row">{"Duplicidades no navegador"}</th><td>{"Consulta participantes uma vez e detecta repetição no arquivo e no evento."}</td><td>{"A consulta local é ignorada; a API continua bloqueando conflitos."}</td></tr>
              <tr><th scope="row">{"Servidor, vagas e transação"}</th><td>{"Sempre executados antes de gravar."}</td><td>{"Idênticos ao modo normal; qualquer conflito cancela o lote."}</td></tr>
            </tbody>
          </Table>
        </Card.Body></Card>
        <div className="platform02-guide-callout">
          <strong>{"Auditoria."}</strong>{" Cada uso do modo de emergência é registrado no log de auditoria do evento. O servidor continua validando e-mail, duplicidade, vagas, limite e a estrutura do payload."}
        </div>
        <div className="platform02-guide-validation">
          <h3>{"Atenção"}</h3>
          <p className="mb-0">{"Formato, obrigatoriedade e opções de campos comuns são regras do navegador no modo normal. Ao ativar a emergência, elas não são executadas localmente; use-a somente com dados já revisados. A coluna email da Plataforma 2, os cabeçalhos, duplicidades, vagas, limite e a transação continuam bloqueando quando inválidos."}</p>
        </div>
      </section>

      <section
        id={guideHeadingId(page.id, PLATFORM02_IMPORT_GUIDE_SECTIONS[4])}
        className="platform02-guide-rich-section"
      >
        <div className="platform02-guide-section-kicker">{"Relatório"}</div>
        <h2>{"Erros, rótulos e correções"}</h2>
        <p>{"Os rótulos e mensagens abaixo são definidos no frontend para manter a mesma orientação em erros locais, na pré-validação e no salvamento. O relatório mostra a linha física da planilha, e-mail, campo, valor enviado, categoria e como corrigir."}</p>
        {Object.entries(errorsByCategory).map(([category, items]) => (
          <Card className="platform02-guide-catalog-card" key={category}><Card.Body>
            <div className="platform02-guide-catalog-heading">
              <div><h3>{category}</h3><p>{`${items.length} tipo(s) de erro.`}</p></div>
              <Badge bg="secondary">{items.length}</Badge>
            </div>
            <Table responsive hover className="platform02-guide-catalog-table mb-0">
              <thead><tr><th scope="col">{"Código"}</th><th scope="col">{"Rótulo"}</th><th scope="col">{"Quando aparece"}</th><th scope="col">{"Como corrigir"}</th></tr></thead>
              <tbody>{items.map((item) => (
                <tr key={item.code}>
                  <th scope="row"><code>{item.code}</code></th>
                  <td>{item.label}</td>
                  <td>{item.when}</td>
                  <td>{item.fix}</td>
                </tr>
              ))}</tbody>
            </Table>
          </Card.Body></Card>
        ))}
      </section>

      <section
        id={guideHeadingId(page.id, PLATFORM02_IMPORT_GUIDE_SECTIONS[5])}
        className="platform02-guide-rich-section"
      >
        <div className="platform02-guide-section-kicker">{"Servidor"}</div>
        <h2>{"Capacidade e transação"}</h2>
        <div className="platform02-guide-operation-grid">
          <article>
            <h3>{"Vagas controladas"}</h3>
            <ul>
              <li>{"A API avalia todas as linhas juntas, acumulando o consumo por opção."}</li>
              <li>{"As linhas de configuração de vagas são bloqueadas durante o salvamento para evitar corrida entre importações."}</li>
              <li>{"Uma única opção esgotada gera relatório e impede qualquer linha de ser gravada."}</li>
            </ul>
          </article>
          <article>
            <h3>{"Tudo ou nada"}</h3>
            <ul>
              <li>{"Participantes, fila de e-mails, JSON do evento e sincronização de vagas ficam na mesma transação."}</li>
              <li>{"Um 409 representa conflito de duplicidade, limite ou vaga; um 422 representa arquivo/configuração inválida."}</li>
              <li>{"Em ambos os casos, corrija o relatório e envie o arquivo novamente."}</li>
            </ul>
          </article>
        </div>
      </section>

      <section
        id={guideHeadingId(page.id, PLATFORM02_IMPORT_GUIDE_SECTIONS[6])}
        className="platform02-guide-rich-section"
      >
        <div className="platform02-guide-section-kicker">{"Antes de salvar"}</div>
        <h2>{"Checklist"}</h2>
        <div className="platform02-guide-validation">
          <ol>
            <li>{"Gere um modelo novo depois de alterar os campos do formulário."}</li>
            <li>{"Mantenha uma única aba, cabeçalho na primeira linha e somente colunas visíveis."}</li>
            <li>{"Confira e-mails, opções, listas de múltipla escolha, datas e números conforme as regras acima."}</li>
            <li>{"Abra o relatório e corrija todos os erros; não há truncamento após 150 itens."}</li>
            <li>{"Se usar emergência, confirme que a base foi revisada e lembre que vagas, duplicidades e limite continuam bloqueando no servidor."}</li>
          </ol>
        </div>
      </section>
    </>
  );
};

const GuideLegacy = ({ page, linkState, onNavigate }) => (
  <>
    <GuideHeader page={page} linkState={linkState} onNavigate={onNavigate} />
    <article className="platform02-guide-legacy-document">
      <section id="legacy-overview">
        <h2>{LEGACY_GUIDE_OVERVIEW.title}</h2>
        <p>{LEGACY_GUIDE_OVERVIEW.intro}</p>
        <h3>Jornada recomendada</h3>
        <ol>{LEGACY_GUIDE_OVERVIEW.journey.map((item) => <li key={item}>{item}</li>)}</ol>
        <h3>Atalhos por tarefa</h3>
        <GuideTable table={{ headers: ["Quero…", "Consulte"], rows: LEGACY_GUIDE_OVERVIEW.quickTasks }} />
        <h3>Escopo</h3>
        {LEGACY_GUIDE_OVERVIEW.scope.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      </section>
      {LEGACY_GUIDE_PAGES.map((legacyPage) => (
        <section className="platform02-guide-legacy-page" id={`legacy-${legacyPage.id}`} key={legacyPage.id}>
          <div className="platform02-guide-eyebrow">{legacyPage.group}</div>
          <h2>{legacyPage.title}</h2>
          <p>{legacyPage.intro}</p>
          {legacyPage.topics.map((topic) => (
            <section id={`legacy-${legacyPage.id}-${guideHeadingId(legacyPage.id, topic.title)}`} key={topic.title}>
              <h3>{topic.title}</h3>
              {topic.path && <div className="platform02-guide-path"><strong>No Painel:</strong> {topic.path}</div>}
              {(topic.paragraphs || []).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              {topic.steps && <ol>{topic.steps.map((item) => <li key={item}>{item}</li>)}</ol>}
              {topic.bullets && <ul>{topic.bullets.map((item) => <li key={item}>{item}</li>)}</ul>}
              {topic.checklist && <ul className="platform02-guide-checklist">{topic.checklist.map((item) => <li key={item}>{item}</li>)}</ul>}
              {topic.table && <GuideTable table={topic.table} />}
              {(topic.callouts || []).map((callout) => (
                <div className="platform02-guide-callout" key={`${callout.title}-${callout.text}`}>
                  <strong>{callout.title}</strong> {callout.text}
                </div>
              ))}
              {topic.validation && (
                <div className="platform02-guide-validation">
                  <h4>Como validar</h4>
                  <ul>{topic.validation.map((item) => <li key={item}>{item}</li>)}</ul>
                </div>
              )}
            </section>
          ))}
        </section>
      ))}
    </article>
  </>
);

const RankingGuide = ({ page, linkState, onNavigate }) => (
  <>
    <GuideHeader page={page} linkState={linkState} onNavigate={onNavigate} />

    <section className="platform02-guide-ranking-intro" aria-labelledby="ranking-guide-overview">
      <div className="platform02-guide-eyebrow">Visão geral</div>
      <h2 id="ranking-guide-overview">Duas jornadas, objetivos diferentes</h2>
      <p>
        O ranking transforma ações válidas em pontos e posição. As conquistas
        reconhecem marcos permanentes, sem acrescentar pontos ao placar.
      </p>
      <Row className="g-3">
        <Col lg={6}>
          <Card className="platform02-guide-concept-card h-100"><Card.Body>
            <span className="platform02-guide-concept-label">Ranking</span>
            <h3>Competição por pontos</h3>
            <p>Exibe os 50 primeiros participantes ativos, inclusive quem ainda tem zero ponto.</p>
            <ul>
              <li>O maior total ocupa a primeira posição.</li>
              <li>Empates compartilham a mesma posição.</li>
              <li>Os valores e o modo de soma pertencem ao evento.</li>
            </ul>
          </Card.Body></Card>
        </Col>
        <Col lg={6}>
          <Card className="platform02-guide-concept-card h-100"><Card.Body>
            <span className="platform02-guide-concept-label">Conquistas</span>
            <h3>Progresso e reconhecimento</h3>
            <p>Apresenta um catálogo global de badges com progresso próprio e conclusão permanente.</p>
            <ul>
              <li>Não altera pontos, total ou posição.</li>
              <li>O registro pode continuar mesmo com a página oculta.</li>
              <li>Uma conquista concluída não é revogada.</li>
            </ul>
          </Card.Body></Card>
        </Col>
      </Row>
    </section>

    <section id={guideHeadingId(page.id, "Sistema de pontuação")} className="platform02-guide-rich-section">
      <div className="platform02-guide-section-kicker">Configuração</div>
      <h2>Sistema de pontuação</h2>
      <p>
        Abra o componente em <strong>Networking → Ranking</strong>. Ali você define
        o que entra no total, os pontos de cada regra e como as conquistas aparecem.
      </p>
      <div className="platform02-guide-path"><strong>No Painel:</strong> Evento → Componentes → Networking → Ranking</div>

      <Row className="g-3 platform02-guide-configuration-grid">
        <Col xl={4} md={6}>
          <Card className="platform02-guide-configuration-card h-100"><Card.Body>
            <span>01</span>
            <h3>Escolha a soma do ranking</h3>
            <dl className="platform02-guide-mode-list">
              <div><dt>Todas as pontuações</dt><dd>Soma todas as regras que concederam pontos.</dd></div>
              <div><dt>Somente atividades</dt><dd>Soma apenas quiz concluído, foto aprovada e vencedor de competição.</dd></div>
            </dl>
          </Card.Body></Card>
        </Col>
        <Col xl={4} md={6}>
          <Card className="platform02-guide-configuration-card h-100"><Card.Body>
            <span>02</span>
            <h3>Defina os valores</h3>
            <p>Use números inteiros de 0 a 1.000.000. Os valores são exclusivos do evento.</p>
            <p><strong>Valor zero:</strong> mantém a regra e o histórico, mas ela passa a valer zero ponto.</p>
          </Card.Body></Card>
        </Col>
        <Col xl={4} md={12}>
          <Card className="platform02-guide-configuration-card h-100"><Card.Body>
            <span>03</span>
            <h3>Controle as conquistas</h3>
            <p><strong>Registrar conquistas</strong> ativa fatos, progresso e conclusões para participantes ativos.</p>
            <p><strong>Exibir conquistas</strong> controla a página, os avisos e as celebrações para o participante.</p>
          </Card.Body></Card>
        </Col>
      </Row>

      <div className="platform02-guide-callout">
        <strong>Salvamentos independentes.</strong> Use <em>Salvar pontuação</em> para
        aplicar os valores e recalcular os totais. Para agregação e opções de
        conquistas, salve o evento. A troca de modo preserva todo o histórico.
      </div>
    </section>

    <section id={guideHeadingId(page.id, "Catálogo de pontuação")} className="platform02-guide-rich-section">
      <div className="platform02-guide-section-kicker">Referência</div>
      <h2>Catálogo de pontuação</h2>
      <p>
        Eventos novos recebem {PLATFORM02_SCORE_RULES.length} fontes de pontuação. Os
        números abaixo são valores iniciais: consulte o componente Ranking para
        confirmar os valores efetivos do evento em edição. O Quiz usa a soma
        verificada dos pontos por acerto e, por isso, não tem valor fixo.
      </p>
      {PLATFORM02_SCORE_GROUPS.map((group) => (
        <Card className="platform02-guide-catalog-card platform02-guide-score-group" key={group.id}><Card.Body>
          <div className="platform02-guide-catalog-heading">
            <div><h3>{group.title}</h3><p>{group.description}</p></div>
            <Badge bg="secondary">{group.items.length}</Badge>
          </div>
          <Table responsive hover className="platform02-guide-catalog-table mb-0">
            <thead><tr><th>Ação ou marco</th><th>Valor inicial</th><th>Quando pontua</th></tr></thead>
            <tbody>{group.items.map((rule) => (
              <tr key={rule.type}>
                <th scope="row">{rule.title}</th>
                <td><Badge bg="dark">{rule.defaultValue === null ? "Variável" : `${rule.defaultValue} pts`}</Badge></td>
                <td>{rule.logic}</td>
              </tr>
            ))}</tbody>
          </Table>
        </Card.Body></Card>
      ))}
      <div className="platform02-guide-callout">
        <strong>Regras personalizadas de atividade.</strong> O operador também pode
        criar valores específicos para foto aprovada, vencedor de competição ou
        vencedor de votação. A regra precisa ser salva antes de ser selecionada na
        atividade; o Quiz continua usando os pontos por acerto das perguntas.
      </div>
    </section>

    <section id={guideHeadingId(page.id, "Catálogo de conquistas")} className="platform02-guide-rich-section">
      <div className="platform02-guide-section-kicker">Referência</div>
      <h2>Catálogo de conquistas</h2>
      <p>
        O catálogo contém {PLATFORM02_ACHIEVEMENT_COUNT} conquistas globais em cinco
        famílias. Ele é igual para todos os eventos e não possui edição no Painel.
      </p>
      <Row className="g-3 platform02-guide-catalog-notes">
        <Col md={6}>
          <div>
            <strong>Progresso separado</strong>
            <span>Conquistas nunca concedem pontos e não interferem na classificação.</span>
          </div>
        </Col>
        <Col md={6}>
          <div>
            <strong>Metas dinâmicas</strong>
            <span>Metas como “todos os pontos” acompanham o conteúdo elegível até serem concluídas.</span>
          </div>
        </Col>
      </Row>
      {PLATFORM02_ACHIEVEMENT_GROUPS.map((group) => (
        <Card className="platform02-guide-catalog-card platform02-guide-achievement-group" key={group.id}><Card.Body>
          <div className="platform02-guide-catalog-heading">
            <div><h3>{group.title}</h3><p>{group.items.length} possibilidades de reconhecimento.</p></div>
            <Badge bg="secondary">{group.items.length}</Badge>
          </div>
          <Table responsive hover className="platform02-guide-catalog-table mb-0">
            <thead><tr><th>Conquista</th><th>Meta</th><th>Como liberar</th></tr></thead>
            <tbody>{group.items.map((item) => (
              <tr key={item.code}><th scope="row">{item.title}</th><td>{item.target}</td><td>{item.logic}</td></tr>
            ))}</tbody>
          </Table>
        </Card.Body></Card>
      ))}
    </section>

    <section id={guideHeadingId(page.id, "Regras de operação")} className="platform02-guide-rich-section">
      <div className="platform02-guide-section-kicker">Operação</div>
      <h2>Regras de operação</h2>
      <div className="platform02-guide-operation-grid">
        <article>
          <h3>Como a classificação se comporta</h3>
          <ul>
            <li>Somente participantes ativos aparecem no ranking.</li>
            <li>Participantes sem pontos continuam elegíveis.</li>
            <li>O ranking mostra até 50 pessoas e mantém uma ordem estável nos empates.</li>
            <li>A reversão da ação de origem pode remover pontos e recalcular o total.</li>
          </ul>
        </article>
        <article>
          <h3>Quando os dados aparecem</h3>
          <ul>
            <li>Ações diretas costumam atualizar o total logo após a confirmação.</li>
            <li>Ações diretas agendam a reconciliação; o fallback do cron roda a cada minuto e revisa fatos recentes.</li>
            <li>Conteúdo ausente não auto-conclui a conquista; evidência canônica já registrada continua valendo.</li>
            <li>Conquistas já concluídas permanecem registradas após mudanças posteriores.</li>
          </ul>
        </article>
      </div>
      <div className="platform02-guide-validation">
        <h3>Como validar antes do evento</h3>
        <ol>
          <li>Use um participante ativo de teste.</li>
          <li>Execute uma ação conhecida e confirme seu valor no catálogo do evento.</li>
          <li>Compare o histórico, o total e a posição depois da atualização.</li>
          <li>Teste também o modo de agregação que será usado no evento.</li>
          <li>Se houver conquistas, confirme separadamente progresso, página e avisos.</li>
        </ol>
      </div>
    </section>
  </>
);

const AvailabilityMark = ({ available, qualified = false }) => {
  const symbol = available ? (qualified ? "✓*" : "✓") : "×";
  const label = available ? "Disponível" : "Não disponível";
  return (
    <span
      className={`platform02-guide-availability-mark ${available ? "is-available" : "is-unavailable"}`}
      aria-label={label}
      title={label}
    >
      {symbol}
    </span>
  );
};

const NotificationsGuide = ({ page, linkState, onNavigate }) => (
  <>
    <GuideHeader page={page} linkState={linkState} onNavigate={onNavigate} />

    <section
      id={guideHeadingId(page.id, PLATFORM02_NOTIFICATION_GUIDE_SECTIONS[0])}
      className="platform02-guide-ranking-intro platform02-guide-notification-intro"
    >
      <div className="platform02-guide-eyebrow">{PLATFORM02_NOTIFICATION_GUIDE_SECTIONS[0]}</div>
      <h2>{"Dois canais, uma mesma origem"}</h2>
      <p>
        {"A plataforma registra acontecimentos importantes na caixa de notificações e, quando o tipo permite, também pode alertar cada dispositivo inscrito por Web Push."}
      </p>
      <Row className="g-3">
        <Col lg={6}>
          <Card className="platform02-guide-concept-card h-100"><Card.Body>
            <span className="platform02-guide-concept-label">{"No aplicativo"}</span>
            <h3>{"Caixa de notificações"}</h3>
            <p>{"Histórico durável vinculado ao participante, com contador, estado de visualização, leitura e destino relacionado."}</p>
            <ul>
              <li>{"Continua disponível depois que o alerta do sistema desaparece."}</li>
              <li>{"Funciona como a fonte oficial do que foi comunicado ao participante."}</li>
              <li>{"Não depende de o participante aceitar Web Push."}</li>
            </ul>
          </Card.Body></Card>
        </Col>
        <Col lg={6}>
          <Card className="platform02-guide-concept-card h-100"><Card.Body>
            <span className="platform02-guide-concept-label">{"Fora do aplicativo"}</span>
            <h3>Web Push</h3>
            <p>{"Alerta opcional exibido pelo navegador e pelo sistema operacional, inclusive com o aplicativo fechado ou fora de foco."}</p>
            <ul>
              <li>{"Exige dispositivo compatível, permissão e inscrição ativas."}</li>
              <li>{"Cada navegador ou dispositivo possui sua própria inscrição."}</li>
              <li>{"Complementa a caixa; não substitui o histórico interno."}</li>
            </ul>
          </Card.Body></Card>
        </Col>
      </Row>
    </section>

    <section
      id={guideHeadingId(page.id, PLATFORM02_NOTIFICATION_GUIDE_SECTIONS[1])}
      className="platform02-guide-rich-section"
    >
      <div className="platform02-guide-section-kicker">{"Fluxo"}</div>
      <h2>{PLATFORM02_NOTIFICATION_GUIDE_SECTIONS[1]}</h2>
      <p>{"A caixa e o Web Push partem da mesma ação confirmada. A gravação, a fila e o contador são coordenados pelo servidor para evitar avisos duplicados ou visíveis antes do dado existir."}</p>
      <Row className="g-3 platform02-guide-configuration-grid platform02-guide-notification-flow">
        {PLATFORM02_NOTIFICATION_FLOW.map((step, index) => (
          <Col xl={4} md={6} key={step.title}>
            <Card className="platform02-guide-configuration-card h-100"><Card.Body>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </Card.Body></Card>
          </Col>
        ))}
      </Row>
      <div className="platform02-guide-callout">
        <strong>{"Ordem garantida."}</strong>{" O contador em tempo real e o início da entrega só ocorrem depois que a transação da notificação foi confirmada."}
      </div>
    </section>

    <section
      id={guideHeadingId(page.id, PLATFORM02_NOTIFICATION_GUIDE_SECTIONS[2])}
      className="platform02-guide-rich-section"
    >
      <div className="platform02-guide-section-kicker">{"Configuração"}</div>
      <h2>{PLATFORM02_NOTIFICATION_GUIDE_SECTIONS[2]}</h2>
      <div className="platform02-guide-path">
        <strong>{"No Painel:"}</strong>{" Evento → Aplicativo → Configurações → Notificações → Ativar sistema de notificação"}
      </div>
      <div className="platform02-guide-operation-grid">
        <article>
          <h3>{"O que o evento controla"}</h3>
          <ul>
            <li>{"A chave geral exibe a campainha, libera a inscrição e permite a entrega de Web Push."}</li>
            <li>{"Desativar a chave interrompe novas entregas externas; registros históricos não são apagados."}</li>
            <li>{"Contas de convidado não recebem inscrição Web Push."}</li>
          </ul>
        </article>
        <article>
          <h3>{"O que o participante controla"}</h3>
          <ul>
            <li>{"A permissão nativa só é solicitada depois de uma ação direta do participante."}</li>
            <li>{"Recusar ou bloquear exige reativação nas configurações do navegador ou do sistema."}</li>
            <li>{"O opt-out é salvo por evento e participante e impede a reinscrição automática ao reconectar."}</li>
            <li>{"Desativar em um aparelho remove somente aquele endpoint; os outros aparelhos continuam independentes."}</li>
          </ul>
        </article>
      </div>
    </section>

    <section
      id={guideHeadingId(page.id, PLATFORM02_NOTIFICATION_GUIDE_SECTIONS[3])}
      className="platform02-guide-rich-section"
    >
      <div className="platform02-guide-section-kicker">{"Dispositivos"}</div>
      <h2>{PLATFORM02_NOTIFICATION_GUIDE_SECTIONS[3]}</h2>
      <p>{"O suporte deve ser verificado no dispositivo real do participante. Versão do sistema, navegador, modo de abertura, economia de bateria e regras corporativas podem alterar o resultado."}</p>
      <Card className="platform02-guide-catalog-card"><Card.Body>
        <Table responsive hover className="platform02-guide-catalog-table platform02-guide-device-table mb-0">
          <thead><tr><th>{"Dispositivo ou modo"}</th><th>{"Suporte"}</th><th>{"Condições"}</th></tr></thead>
          <tbody>
            {PLATFORM02_NOTIFICATION_DEVICE_COMPATIBILITY.map((item) => (
              <tr key={item.id}>
                <th scope="row"><strong>{item.device}</strong><small>{item.summary}</small></th>
                <td><AvailabilityMark available={item.available} qualified={item.id === "ios"} /></td>
                <td>{item.requirements}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body></Card>
      <div className="platform02-guide-callout">
        <strong>{"iPhone e iPad."}</strong>{" A partir do iOS/iPadOS 16.4, o Web Push funciona para web apps adicionados à Tela de Início. Abrir apenas uma aba do Safari ou de outro navegador não basta."}
      </div>
    </section>

    <section
      id={guideHeadingId(page.id, PLATFORM02_NOTIFICATION_GUIDE_SECTIONS[4])}
      className="platform02-guide-rich-section"
    >
      <div className="platform02-guide-section-kicker">{"Referência"}</div>
      <h2>{PLATFORM02_NOTIFICATION_GUIDE_SECTIONS[4]}</h2>
      <p>
        {"A lista abaixo cobre os "}<strong>{PLATFORM02_NOTIFICATION_CATALOG.length}</strong>{" tipos reconhecidos pelo sistema. ✓ indica suporte do recurso; × indica que o fluxo atual registra somente na caixa ou existe apenas para compatibilidade histórica."}
      </p>
      <div className="platform02-guide-notification-legend" aria-label="Legenda do catálogo">
        <span><AvailabilityMark available />{" Disponível"}</span>
        <span><AvailabilityMark available={false} />{" Não enviado por este canal"}</span>
      </div>
      <Card className="platform02-guide-catalog-card platform02-guide-notification-catalog-card"><Card.Body>
        <div className="platform02-guide-catalog-heading">
          <div>
            <h3>{"Catálogo unificado"}</h3>
            <p>{"A coluna Web Push indica capacidade do tipo, não confirmação de entrega em todos os aparelhos."}</p>
          </div>
          <Badge bg="secondary">{PLATFORM02_NOTIFICATION_CATALOG.length}</Badge>
        </div>
        <Table responsive hover className="platform02-guide-catalog-table platform02-guide-notification-catalog mb-0">
          <thead><tr><th>{"Notificação"}</th><th>Web Push</th></tr></thead>
          <tbody>
            {PLATFORM02_NOTIFICATION_CATALOG.map((item) => (
              <tr key={item.type}>
                <th scope="row">
                  <div className="platform02-guide-notification-name">
                    <AvailabilityMark available />
                    <span>
                      <Badge bg="light" text="dark">{item.category}</Badge>
                      <strong>{item.title}</strong>
                      <small>{item.description}</small>
                    </span>
                  </div>
                </th>
                <td>
                  <div className="platform02-guide-notification-push">
                    <AvailabilityMark available={item.webPush} />
                    <span>{item.pushReason || (item.webPush ? "Disponível quando o evento e o dispositivo estão habilitados." : "Não enviado por Web Push.")}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body></Card>
    </section>

    <section
      id={guideHeadingId(page.id, PLATFORM02_NOTIFICATION_GUIDE_SECTIONS[5])}
      className="platform02-guide-rich-section"
    >
      <div className="platform02-guide-section-kicker">{"Operação"}</div>
      <h2>{PLATFORM02_NOTIFICATION_GUIDE_SECTIONS[5]}</h2>
      <div className="platform02-guide-operation-grid">
        <article>
          <h3>{"Histórico e contador"}</h3>
          <ul>
            <li>{"O contador vem do servidor e não depende apenas da primeira página em cache."}</li>
            <li>{"Abrir a caixa atualiza a visualização; a leitura individual permanece registrada."}</li>
            <li>{"O participante não oculta nem apaga itens já entregues."}</li>
          </ul>
        </article>
        <article>
          <h3>{"Chat e agrupamento"}</h3>
          <ul>
            <li>{"A caixa mantém um item atual por conversa para evitar poluição."}</li>
            <li>{"Cada mensagem conserva identidade própria na fila Web Push."}</li>
            <li>{"Conversas abertas, sessões silenciadas ou presença ativa podem suprimir o alerta externo."}</li>
          </ul>
        </article>
        <article>
          <h3>{"Tentativas de entrega"}</h3>
          <ul>
            <li>{"Falhas temporárias são reenfileiradas com espera progressiva."}</li>
            <li>{"Endpoints expirados ou removidos são revogados somente no dispositivo afetado."}</li>
            <li>{"Aceite do provedor não comprova exibição nem leitura humana."}</li>
          </ul>
        </article>
        <article>
          <h3>{"Analytics e retenção"}</h3>
          <ul>
            <li>{"Cliques e aberturas usam uma identidade assinada e estável."}</li>
            <li>{"Não existe métrica confiável de impressão na tela."}</li>
            <li>{"Trabalhos concluídos ou suprimidos e falhas terminais possuem limpeza operacional; o histórico da caixa é independente."}</li>
          </ul>
        </article>
      </div>
    </section>

    <section
      id={guideHeadingId(page.id, PLATFORM02_NOTIFICATION_GUIDE_SECTIONS[6])}
      className="platform02-guide-rich-section"
    >
      <div className="platform02-guide-section-kicker">{"Checklist"}</div>
      <h2>{PLATFORM02_NOTIFICATION_GUIDE_SECTIONS[6]}</h2>
      <div className="platform02-guide-operation-grid">
        <article>
          <h3>{"Boas práticas"}</h3>
          <ul>
            <li>{"Explique o benefício antes de pedir permissão e use um botão acionado pelo participante."}</li>
            <li>{"Reserve Web Push para acontecimentos relevantes e com destino claro."}</li>
            <li>{"Evite usar alertas para confirmar ações que o participante acabou de concluir na mesma tela."}</li>
            <li>{"Teste título, texto, imagem e deep link antes da abertura do evento."}</li>
          </ul>
        </article>
        <article>
          <h3>{"Limites que devem ser comunicados"}</h3>
          <ul>
            <li>{"Modo Foco, Não Perturbe, economia de bateria e políticas corporativas podem atrasar ou ocultar alertas."}</li>
            <li>{"Limpar dados, trocar de navegador ou reinstalar o PWA cria ou remove uma inscrição."}</li>
            <li>{"Web Push depende de rede; a caixa será sincronizada quando o participante voltar ao aplicativo."}</li>
          </ul>
        </article>
      </div>
      <div className="platform02-guide-validation">
        <h3>{"Como validar antes do evento"}</h3>
        <ol>
          <li>{"Ative o sistema de notificação e use um participante ativo, não convidado."}</li>
          <li>{"Teste em HTTPS e confirme que o service worker está ativo."}</li>
          <li>{"Conceda permissão por um clique do participante e confirme a inscrição naquele aparelho."}</li>
          <li>{"Gere um item do catálogo, confira a caixa, o contador e o destino do clique."}</li>
          <li>{"Repita em segundo aparelho e confirme que desativar um deles não remove o outro."}</li>
          <li>{"No iPhone/iPad, instale na Tela de Início antes de solicitar permissão."}</li>
          <li>{"Se não chegar, verifique permissão, opt-out, modo Foco, navegador, inscrição e logs da fila/provedor."}</li>
        </ol>
      </div>
    </section>
  </>
);

const GuidePageNavigation = ({ page, linkState, onNavigate }) => {
  const index = GUIDE_PAGES.findIndex((item) => item.id === page.id);
  const previous = index > 0 ? GUIDE_PAGES[index - 1] : null;
  const next = index < GUIDE_PAGES.length - 1 ? GUIDE_PAGES[index + 1] : null;
  if (!previous && !next) return null;
  return (
    <nav className="platform02-guide-page-navigation" aria-label="Páginas anterior e próxima">
      {previous ? (
        <HelpLink to={previous.href} {...guideLinkProps(linkState, onNavigate)}>
          <FontAwesomeIcon icon={faArrowLeft} /><span><small>Anterior</small>{previous.title}</span>
        </HelpLink>
      ) : <span />}
      {next && (
        <HelpLink to={next.href} className="text-end" {...guideLinkProps(linkState, onNavigate)}>
          <span><small>Próxima</small>{next.title}</span><FontAwesomeIcon icon={faArrowRight} />
        </HelpLink>
      )}
    </nav>
  );
};

export const getGuideIndexItems = (page) => {
  if (page.id === "component-ranking") {
    return ["Sistema de pontuação", "Catálogo de pontuação", "Catálogo de conquistas", "Regras de operação"];
  }
  if (page.kind === "legacy") return [];
  return page.sections || [];
};

export const Platform02GuidePage = ({ page, linkState, onNavigate }) => (
  <div className="platform02-guide-page">
    {page.kind === "home" && <GuideHome linkState={linkState} onNavigate={onNavigate} />}
    {(page.kind === "hub" || page.kind === "module") && <GuideHub page={page} linkState={linkState} onNavigate={onNavigate} />}
    {page.kind === "article" && !["component-ranking", "component-communications", "event-import"].includes(page.id) && <GuideArticle page={page} linkState={linkState} onNavigate={onNavigate} />}
    {page.id === "event-import" && <ImportGuide page={page} linkState={linkState} onNavigate={onNavigate} />}
    {page.id === "component-ranking" && <RankingGuide page={page} linkState={linkState} onNavigate={onNavigate} />}
    {page.id === "component-communications" && <NotificationsGuide page={page} linkState={linkState} onNavigate={onNavigate} />}
    {page.kind === "legacy" && <GuideLegacy page={page} linkState={linkState} onNavigate={onNavigate} />}
    {page.kind !== "home" && <GuidePageNavigation page={page} linkState={linkState} onNavigate={onNavigate} />}
  </div>
);
